import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { ChevronDown, X } from "lucide-react";
import { Order } from "../../../../services";
import style from "./RebidTenderModal.module.scss";

const DURATION_OPTIONS = [
  { minutes: 15, labelKey: "orderInfoClient.rebid.durations.minutes15" },
  { minutes: 30, labelKey: "orderInfoClient.rebid.durations.minutes30" },
  { minutes: 45, labelKey: "orderInfoClient.rebid.durations.minutes45" },
  { minutes: 60, labelKey: "orderInfoClient.rebid.durations.hour1" },
  { minutes: 75, labelKey: "orderInfoClient.rebid.durations.hour1Minutes15" },
  { minutes: 90, labelKey: "orderInfoClient.rebid.durations.hour1Minutes30" },
  { minutes: 105, labelKey: "orderInfoClient.rebid.durations.hour1Minutes45" },
  { minutes: 120, labelKey: "orderInfoClient.rebid.durations.hours2" },
];

const RebidTenderModal = ({ isOpen, orderId, onSuccess, onCancel, onSubmit = Order.rebidTender }) => {
  const { t } = useTranslation();
  const [selectedMinutes, setSelectedMinutes] = useState(null);
  const [isSelectOpen, setIsSelectOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setSelectedMinutes(null);
      setIsSelectOpen(false);
      setIsLoading(false);
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key !== "Escape") return;

      if (isSelectOpen) {
        setIsSelectOpen(false);
      } else {
        onCancel?.();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, isSelectOpen, onCancel]);

  if (!isOpen) return null;

  const selectedOption = DURATION_OPTIONS.find(
    (option) => option.minutes === selectedMinutes,
  );

  const handleConfirm = async () => {
    if (selectedMinutes == null) {
      toast.error(t("orderInfoClient.rebid.durationRequired"));
      return;
    }

    try {
      setIsLoading(true);
      const tenderDateEnd = new Date(
        Date.now() + selectedMinutes * 60 * 1000,
      ).toISOString();

      await onSubmit(orderId, tenderDateEnd);
      toast.success(t("orderInfoClient.rebid.success"));
      onSuccess?.();
    } catch (error) {
      toast.error(t("common.genericError"));
    } finally {
      setIsLoading(false);
    }
  };

  return ReactDOM.createPortal(
    <div
      className={style.overlay}
      data-portal-modal="true"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isLoading) onCancel?.();
      }}
    >
      <div
        className={style.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="rebid-tender-title"
      >
        <div className={style.header}>
          <h2 id="rebid-tender-title" className={style.title}>
            {t("orderInfoClient.rebid.title")}
          </h2>
          <button
            type="button"
            className={style.closeButton}
            onClick={onCancel}
            disabled={isLoading}
            aria-label={t("common.close")}
          >
            <X size={22} aria-hidden="true" />
          </button>
        </div>

        <p className={style.description}>
          {t("orderInfoClient.rebid.description")}
        </p>

        <div className={style.selectWrapper}>
          <span className={style.selectLabel}>
            {t("orderInfoClient.rebid.durationLabel")}
          </span>
          <button
            type="button"
            className={style.selectButton}
            onClick={() => setIsSelectOpen((isOpen) => !isOpen)}
            aria-haspopup="listbox"
            aria-expanded={isSelectOpen}
            disabled={isLoading}
          >
            <span>
              {selectedOption
                ? t(selectedOption.labelKey)
                : t("orderInfoClient.rebid.placeholder")}
            </span>
            <ChevronDown
              size={18}
              className={isSelectOpen ? style.chevronOpen : undefined}
              aria-hidden="true"
            />
          </button>

          {isSelectOpen && (
            <div
              className={style.options}
              role="listbox"
              aria-label={t("orderInfoClient.rebid.durationLabel")}
            >
              {DURATION_OPTIONS.map((option) => (
                <button
                  type="button"
                  role="option"
                  aria-selected={selectedMinutes === option.minutes}
                  className={style.option}
                  key={option.minutes}
                  onClick={() => {
                    setSelectedMinutes(option.minutes);
                    setIsSelectOpen(false);
                  }}
                >
                  {t(option.labelKey)}
                </button>
              ))}
            </div>
          )}
        </div>

        <div className={style.actions}>
          <button
            type="button"
            className={style.cancelButton}
            onClick={onCancel}
            disabled={isLoading}
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            className={style.confirmButton}
            onClick={handleConfirm}
            disabled={isLoading}
          >
            {t("orderInfoClient.rebid.confirm")}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default RebidTenderModal;
